import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, FormHelperText, TextField, Autocomplete, Divider } from '@mui/material';
import { setSuccessfullModifyDialog } from '../../../redux/successfullModifyDialogSlice';
import { setUserDetailsData } from '../../../redux/userDetailsDataSlice';
import { CustomErrorLogger } from '../../../utils/CustomErrorLogger';
import { canSelectUserRegions } from '../../../utils/regionManagementRights';
import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import '../../../css/users/addUserDialog.css';
import { useParams } from 'react-router-dom';
import Iconify from '../../Iconify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';

export const UpdateUserLocalisation = ({ updateUserLocalisation, setUpdateUserLocalisation }) => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { id } = useParams();
    const authenticatedUser = useSelector(state => state.AuthenticatedUser);
    const userDetailsData = useSelector((state) => state.UserDetailsData);

    const [regions, setRegions] = useState([]);
    const [sites, setSites] = useState([]);
    const [poles, setPoles] = useState([]);
    const [metiers, setMetiers] = useState([]);

    const formik = useFormik({
        initialValues: {
            selectedRegion: userDetailsData?.user_region?.map(region => region.region.id),
            selectedSite: userDetailsData?.user_site?.map(site => site.site.id),
            selectedPole: userDetailsData?.user_pole?.map(pole => pole.pole.id),
            selectedMetier: userDetailsData?.user_metier?.map(metier => metier.metier.id),
        },
        validationSchema: Yup.object().shape({
            selectedRegion: Yup.array()
                .min(1, t('Le champ région doit contenir au moins un élément'))
                .required(t('Le champ région doit contenir au moins un élément')),
        }),
        onSubmit: async (values) => {
            try {
                const response = await axios.patch(`user/user_localisation/${id}`, values);
                if (response.status === 200 || response.status === 201) {
                    setUpdateUserLocalisation(false);
                    dispatch(setSuccessfullModifyDialog({ open: true, type: 'Modifier la localisation et les métiers' }));
                    dispatch(setUserDetailsData({ ...userDetailsData, user_region: response.data.user_region, user_pole: response.data.user_pole, user_site: response.data.user_site, user_metier: response.data.user_metier }));
                }
            } catch (error) {
                console.error('Error updating user:', error);
                CustomErrorLogger(error, 'error updating user in UpdateUserLocalisation');
            }
        },
    });

    const { handleSubmit, values, handleBlur, touched, errors, setFieldValue } = formik;

    const handleClose = useCallback(() => {
        setUpdateUserLocalisation(false);
    }, [setUpdateUserLocalisation]);

    const fetchRegions = useCallback(async () => {
        try {
            const response = await axios.get('region');
            if (response.status === 200 || response.status === 201) {
                setRegions(response.data);
            }
        } catch (error) {
            console.error('Error fetching regions:', error);
            CustomErrorLogger(error, 'fetch regions in UpdateUserLocalisation');
        }
    }, []);

    const fetchSites = useCallback(async () => {
        if (values.selectedRegion.length === 0) {
            setSites([]);
            setPoles([]);
            setMetiers([]);
            return;
        }
        try {
            const response = await axios.get('site', { params: { regions: values.selectedRegion } });
            if (response.status === 200 || response.status === 201) {
                setSites(response.data);
            }
        } catch (error) {
            console.error('Error Fetching sites:', error);
            CustomErrorLogger(error, 'fetch sites in UpdateUserLocalisation');
        }
    }, [values.selectedRegion])

    const fetchPoles = useCallback(async () => {
        if (values.selectedSite.length === 0) {
            setPoles([]);
            setMetiers([]);
            return;
        }
        try {
            const response = await axios.get('pole', { params: { sites: values.selectedSite } });
            if (response.status === 200 || response.status === 201) {
                setPoles(response.data);
            }
        } catch (error) {
            console.error('Error Fetching poles:', error);
            CustomErrorLogger(error, 'fetch poles in UpdateUserLocalisation');
        }
    }, [values.selectedSite]);

    const fetchMetiers = useCallback(async () => {
        if (values.selectedSite.length === 0) {
            setMetiers([]);
            return;
        }
        try {
            const response = await axios.get('metier', { params: { poles: values.selectedPole, sites: values.selectedSite } });
            if (response.status === 200 || response.status === 201) {
                setMetiers(response.data);
            }
        } catch (error) {
            console.error('Error Fetching metiers:', error);
            CustomErrorLogger(error, 'fetch metiers in UpdateUserLocalisation');
        }
    }, [values.selectedPole, values.selectedSite]);

    useEffect(() => {
        fetchRegions()
    }, [fetchRegions]);

    useEffect(() => {
        fetchSites()
    }, [fetchSites, values.selectedRegion]);

    useEffect(() => {
        fetchPoles()
    }, [fetchPoles, values.selectedSite]);

    useEffect(() => {
        fetchMetiers()
    }, [fetchMetiers, values.selectedSite, values.selectedPole]);

    return (
        <Dialog open={updateUserLocalisation} fullWidth>
            <IconButton onClick={handleClose} className='addUser_icon_button'>
                <Iconify icon={"material-symbols:close"} className='addUser_close_icon' />
            </IconButton>
            <DialogTitle className='addUser_dialog_title'>{t('Modifier la localisation et les métiers')}</DialogTitle>
            <Divider />
            <form onSubmit={handleSubmit}>
                <DialogContent className='addUser_dialog_content'>
                    {canSelectUserRegions(authenticatedUser) &&
                        <Box className='addUser_helper_selct_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Region')}</FormHelperText>
                            <Autocomplete
                                multiple
                                sx={{ padding: 0 }}
                                options={regions}
                                getOptionLabel={(option) => option.name}
                                disableCloseOnSelect={true}
                                value={regions.filter(region => values.selectedRegion.includes(region.id))}
                                onChange={(event, newValue) => {
                                    const selectedRegionIds = newValue?.map(region => region.id);
                                    setFieldValue('selectedRegion', selectedRegionIds);
                                    setFieldValue('selectedSite', []);
                                    setFieldValue('selectedPole', []);
                                    setFieldValue('selectedMetier', []);
                                }}
                                onBlur={handleBlur}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        className='addUser_InputField'
                                        error={touched.selectedRegion && Boolean(errors.selectedRegion)}
                                        helperText={touched.selectedRegion && errors.selectedRegion}
                                        InputProps={{
                                            ...params.InputProps,
                                            style: {
                                                minHeight: 'fit-content',
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    }
                    <Box className='addUser_helper_selct_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Site')}</FormHelperText>
                        <Autocomplete
                            multiple
                            sx={{ padding: 0 }}
                            options={sites}
                            getOptionLabel={(option) => option.name}
                            disableCloseOnSelect={true}
                            value={sites.filter(site => values.selectedSite.includes(site.id))}
                            onChange={(event, newValue) => {
                                const selectedSiteIds = newValue?.map(site => site.id);
                                setFieldValue('selectedSite', selectedSiteIds);
                                setFieldValue('selectedPole', []);
                                setFieldValue('selectedMetier', []);
                            }}
                            onBlur={handleBlur}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    className='addUser_InputField'
                                    error={touched.selectedSite && Boolean(errors.selectedSite)}
                                    helperText={touched.selectedSite && errors.selectedSite}
                                    InputProps={{
                                        ...params.InputProps,
                                        style: {
                                            minHeight: 'fit-content',
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                    <Box className='addUser_helper_selct_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Pole')}</FormHelperText>
                        <Autocomplete
                            multiple
                            sx={{ padding: 0 }}
                            options={poles}
                            getOptionLabel={(option) => option.name}
                            disableCloseOnSelect={true}
                            value={poles.filter(pole => values.selectedPole.includes(pole.id))}
                            onChange={(event, newValue) => {
                                const selectedPoleIds = newValue?.map(pole => pole.id);
                                setFieldValue('selectedPole', selectedPoleIds);
                                setFieldValue('selectedMetier', []);
                            }}
                            onBlur={handleBlur}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    className='addUser_InputField'
                                    error={touched.selectedPole && Boolean(errors.selectedPole)}
                                    helperText={touched.selectedPole && errors.selectedPole}
                                    InputProps={{
                                        ...params.InputProps,
                                        style: {
                                            minHeight: 'fit-content',
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                    <Box className='addUser_helper_selct_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Metier')}</FormHelperText>
                        <Autocomplete
                            multiple
                            sx={{ padding: 0 }}
                            options={metiers}
                            getOptionLabel={(option) => option.name}
                            disableCloseOnSelect={true}
                            value={metiers.filter(metier => values.selectedMetier.includes(metier.id))}
                            onChange={(event, newValue) => {
                                const selectedMetierIds = newValue?.map(metier => metier.id);
                                setFieldValue('selectedMetier', selectedMetierIds);
                            }}
                            onBlur={handleBlur}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    className='addUser_InputField'
                                    error={touched.selectedMetier && Boolean(errors.selectedMetier)}
                                    helperText={touched.selectedMetier && errors.selectedMetier}
                                    InputProps={{
                                        ...params.InputProps,
                                        style: {
                                            minHeight: 'fit-content',
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                </DialogContent>

                <DialogActions className='addUser_dialog_actions'>
                    <Button className='addUser_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <Button className='addUser_button_valider' variant="contained" type="submit">{t('Valider')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};